/* eslint-disable no-restricted-syntax */
import * as React from 'react';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import { Typography2 } from '@coursera/cds-core';
import { Box } from '@coursera/coursera-ui';

import { Heading, Spacer } from 'bundles/enterprise-ui/components';
import type { AutoEnrollmentRow } from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/AutoEnrollmentEditTriggerModal';
import type { AutoEnrollmentTriggersQuery_ProgramEnrollmentSettingsV1Resource_byTargetProgram_elements as Settings } from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/__generated__/AutoEnrollmentTriggersQuery';
import { TriggerEvent } from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/constants/AutoEnrollmentConstants';
import Table from 'bundles/internal-ui/components/table/Table';

import _t from 'i18n!nls/internal-enterprise';

type Props = {
  enrollmentSettings: (Settings | null)[];
  program: { id: string; name: string };
  deleteTrigger: (row: AutoEnrollmentRow) => void;
  editTrigger: (row: AutoEnrollmentRow) => void;
};

const getRows = (enrollmentSettings: $TSFixMe) => {
  const rows: AutoEnrollmentRow[] = [];

  enrollmentSettings.forEach((setting: $TSFixMe) => {
    const template = {
      enrollIn: setting.enrollmentTargetCourse?.name,
      courseId: setting.enrollmentTargetCourse?.id,
      id: setting?.id,
      targetProgramId: setting.enrollmentTargetProgram?.id,
    };

    if (setting.triggerEventJoinedProgram) {
      rows.push({
        ...template,
        enrollmentTrigger: 'On joining program',
        triggeredByCourse: 'Not Applicable',
        triggeredCourseId: '',
        triggerType: TriggerEvent.JOIN_PROGRAM,
      });
    }

    if (setting.triggerEventCompletedCourse) {
      rows.push({
        ...template,
        enrollmentTrigger: 'On completion of',
        triggerType: TriggerEvent.COMPLETE_COURSE,
        triggeredByCourse: setting.triggerEventCompletedCourse?.name,
        triggeredCourseId: setting.triggerEventCompletedCourse?.id,
      });
    }

    if (setting.triggerEventEnrolledCourse) {
      rows.push({
        ...template,
        enrollmentTrigger: 'On enrollment of',
        triggerType: TriggerEvent.ENROLL_IN_COURSE,
        triggeredByCourse: setting.triggerEventEnrolledCourse?.name,
        triggeredCourseId: setting.triggerEventEnrolledCourse?.id,
      });
    }
  });

  return rows;
};

const getColumns = () => {
  return [
    { key: 'enrollIn', name: _t('Enroll Learners in Course') },
    { key: 'courseId', name: _t('Course Id') },
    { key: 'enrollmentTrigger', name: _t('Based on Enrollment Trigger') },
    { key: 'triggeredByCourse', name: _t('Triggered By Course') },
    { key: 'triggeredCourseId', name: _t('Course Id') },
  ];
};

const getActions = (deleteTrigger: $TSFixMe, editTrigger: $TSFixMe) => {
  return [
    {
      icon: 'edit',
      onClick: (row: $TSFixMe) => {
        editTrigger(row);
      },
    },
    {
      icon: 'clear',
      onClick: (row: $TSFixMe) => {
        deleteTrigger(row);
      },
    },
  ];
};

const AutoEnrollmentTable: React.FC<Props> = ({ enrollmentSettings, program, deleteTrigger, editTrigger }) => {
  if (enrollmentSettings.length === 0) {
    return (
      <Box alignItems="center" justifyContent="center">
        <Typography2 component="p">
          <FormattedHTMLMessage message={_t(`No Auto-Enrollment Rules for {program}`)} program={program.name} />
        </Typography2>
      </Box>
    );
  }

  return (
    <Box alignItems="center" justifyContent="center" flexDirection="column">
      <Heading>
        <FormattedHTMLMessage message={_t(`Auto-Enrollment Rules for {program}`)} program={program.name} />
      </Heading>
      <Spacer size={24} />
      <Table
        columns={getColumns()}
        rows={getRows(enrollmentSettings)}
        actions={getActions(deleteTrigger, editTrigger)}
        initialSortColumn="enrollIn"
        rowId={(row) => row.id}
      />
      <Spacer size={32} />
    </Box>
  );
};

export default AutoEnrollmentTable;
